import { ISolution } from "@/interfaces/solution";
import SolutionCategoryModel from "./solution_category.model";
import SolutionInsurerPlanModel from "./solution_insurer_plan.model";
import SolutionNeedModel from "./solution_need.model";
import SolutionQualificationModel from "./solution_qualification.model";
import SolutionFavoriteCorporateClientModel from "./solution_favorite_corporate_client.model";
import { CrudModel } from "./_crud";
import { crudRead } from "./_request";

export interface BulkAssignPlansRequest {
	solutionIds: string[];
	insurerPlanIds: string[];
}

export interface BulkAssignPlansResponse {
	created: number;
	alreadyExisted: number;
	total: number;
	message: string;
	solutionNames?: string[];
	planNames?: string[];
}

// Interfaces para página de associação
export interface GetSolutionsByPlanParams {
	planIds: string[];
	page?: number;
	limit?: number;
	status?: string[];
	categoryId?: string;
	corporateClientId?: string;
	isFavorite?: boolean;
	isAssociated?: boolean;
	searchParam?: string;
}

export interface SolutionWithAssociation {
	id: string;
	name: string;
	status: string;
	isFavorite: boolean;
	category: { id: string; name: string };
	isAssociated: boolean;
}

export interface PaginatedSolutionsResponse {
	data: SolutionWithAssociation[];
	total: number;
	page: number;
	limit: number;
	hasMore: boolean;
}

export interface BulkUnassignPlanRequest {
	solutionIds: string[];
	insurerPlanId: string;
}

export interface BulkUnassignPlanResponse {
	removed: number;
	notFound: number;
	message: string;
}

// Interfaces para favoritos por corporativo
export interface AssignFavoriteCorporateRequest {
	solutionIds: string[];
	corporateClientIds: string[];
}

export interface RemoveFavoriteCorporateRequest {
	solutionIds: string[];
	corporateClientIds: string[];
}

export interface FavoriteOperationResponse {
	processed: number;
	duplicates?: number;
	notFound?: number;
	message: string;
}

export interface FavoriteCorporateClient {
	id: string;
	name: string;
	cnpj: string;
	favoritedAt: Date;
}

export class SolutionModel extends CrudModel {
	constructor() {
		super(
			"admin/solution",
			[],
			[
				{ fieldKey: "category", model: SolutionCategoryModel },
				{ fieldKey: "solutionNeeds", model: SolutionNeedModel },
				{ fieldKey: "solutionQualifications", model: SolutionQualificationModel },
				{ fieldKey: "solutionInsurerPlans", model: SolutionInsurerPlanModel },
				{ fieldKey: "favoriteCorporateClients", model: SolutionFavoriteCorporateClientModel },
			],
		);
	}

	updateSolutionStatus(id: string, solution: Pick<ISolution, "status">) {
		return this.patch(null, `${id}/validate`, solution);
	}

	async bulkAssignPlans(data: BulkAssignPlansRequest): Promise<BulkAssignPlansResponse> {
		return this.post(null, "bulk-assign-plans", data);
	}

	async getSolutionsByPlan(params: GetSolutionsByPlanParams): Promise<PaginatedSolutionsResponse> {
		const queryParams = new URLSearchParams();
		if (params.planIds?.length) {
			params.planIds.forEach((planId: string) => queryParams.append("planIds", planId));
		}
		if (params.page) queryParams.append("page", String(params.page));
		if (params.limit) queryParams.append("limit", String(params.limit));
		if (params.status?.length) params.status.forEach(s => queryParams.append("status", s));
		if (params.categoryId) queryParams.append("categoryId", params.categoryId);
		if (params.corporateClientId) queryParams.append("corporateClientId", params.corporateClientId);
		if (params.isFavorite !== undefined) queryParams.append("isFavorite", String(params.isFavorite));
		if (params.isAssociated !== undefined) queryParams.append("isAssociated", String(params.isAssociated));
		if (params.searchParam) queryParams.append("searchParam", params.searchParam);

		return this.get(`by-plan?${queryParams.toString()}`);
	}

	async bulkUnassignPlan(data: BulkUnassignPlanRequest): Promise<BulkUnassignPlanResponse> {
		return this.post(null, "bulk-unassign-plan", data);
	}

	// Métodos para gerenciar favoritos por corporativo
	async getFavoriteCorporateClients(solutionId: string): Promise<FavoriteCorporateClient[]> {
		return this.get(`${solutionId}/favorite-corporates`);
	}

	async getFavoriteSolutions(corporateClientId: string): Promise<any[]> {
		return this.get(`favorites/by-corporate/${corporateClientId}`);
	}

	async assignFavorites(data: AssignFavoriteCorporateRequest): Promise<FavoriteOperationResponse> {
		return this.post(null, "favorites/assign", data);
	}

	async removeFavorites(data: RemoveFavoriteCorporateRequest): Promise<FavoriteOperationResponse> {
		return this.post(null, "favorites/remove", data);
	}

	async saveFavoriteCorporateClients(solutionId: string, corporateClientIds: string[]): Promise<void> {
		// Buscar favoritos atuais
		const currentFavorites = await this.getFavoriteCorporateClients(solutionId);
		const currentIds = currentFavorites.map(fav => fav.id);

		// Determinar quais adicionar e remover
		const toAdd = corporateClientIds.filter(id => !currentIds.includes(id));
		const toRemove = currentIds.filter(id => !corporateClientIds.includes(id));

		// Adicionar novos favoritos
		if (toAdd.length > 0) {
			await this.assignFavorites({
				solutionIds: [solutionId],
				corporateClientIds: toAdd,
			});
		}

		// Remover favoritos
		if (toRemove.length > 0) {
			await this.removeFavorites({
				solutionIds: [solutionId],
				corporateClientIds: toRemove,
			});
		}
	}

	override async getJoinableFields(): Promise<{ field: string }[]> {
		const joins = await super.getJoinableFields();

		// Garantir que favoriteCorporateClients sempre está incluído
		const hasFavoriteCorporateClients = joins.some(j => j.field === "favoriteCorporateClients");
		if (!hasFavoriteCorporateClients) {
			joins.push({ field: "favoriteCorporateClients" });
		}

		const hasFavoriteCorporateClientsCorporateClient = joins.some(
			j => j.field === "favoriteCorporateClients.corporateClient",
		);
		if (!hasFavoriteCorporateClientsCorporateClient) {
			joins.push({ field: "favoriteCorporateClients.corporateClient" });
		}

		return joins;
	}

	override async read(id: string | number, joinKey?: string | null, joinBlacklist?: string[]): Promise<any> {
		const queryParameters = {
			// join: await this.getJoinableFields(),
			// sort: { field: "availabilities.startDateTime", order: "ASC" },
		};
		return crudRead(this.endpoint, id, queryParameters);
	}
}

export default new SolutionModel();
